import type {
    ContentBlock,
    IContextEnrichmentDataPart,
} from "@/api/chat/chat.types";

/**
 * One per report row, in the assistant message that answers the row's user
 * message (`chatMessageId`). The card reads the row from the report list.
 */
export interface IReportDataPart {
    conversationId: number;
    chatMessageId: number;
}

export const REPORT_DATA_PART_NAME = "report";

export function reportDataPart(data: IReportDataPart) {
    return { type: "data" as const, name: REPORT_DATA_PART_NAME, data };
}

/**
 * Optional annotations layered onto the emitted content part when the
 * caller (e.g. `convertMessages`) has conversation-level context.
 *
 * - `isLatestActionable`: true only on the most-recent non-dismissed /
 *   non-refined enrichment across the whole history; earlier cards render
 *   in the collapsed footer.
 * - `conversationId` + `messageId`: the host conversation and assistant
 *   message ids — required by the card so dismiss / refine endpoint calls
 *   don't have to reach into the router or a separate context.
 */
export interface MapContentBlockOptions {
    isLatestActionable?: boolean;
    conversationId?: number;
    messageId?: number;
}

export function mapContentBlock(
    block: ContentBlock,
    options: MapContentBlockOptions = {}
): unknown {
    switch (block.type) {
        case "text":
            return { type: "text", text: block.text };

        case "tool_use":
            return {
                type: "tool-call",
                toolCallId: block.id,
                toolName: block.name,
                args: block.input,
                argsText: JSON.stringify(block.input),
            };

        case "thinking":
            return { type: "reasoning", text: block.thinking };

        case "processing":
            return {
                type: "data",
                name: "processing",
                data: {
                    name: block.name,
                    status: block.status,
                    summary_text: block.summary_text,
                },
            };

        case "context_enrichment": {
            // Defaults mirror the live-stream adapter (which receives a
            // `Partial<ContentBlock>` from `IAgenticStreamEvent`), so both
            // emission paths produce identical shapes even if a caller
            // hands us a partially-hydrated block.
            const data: IContextEnrichmentDataPart = {
                chips: block.chips ?? [],
                freeTextPrompt: block.free_text_prompt ?? "",
                dismissed: block.dismissed ?? false,
                refined: block.refined ?? false,
                isLatestActionable: options.isLatestActionable ?? false,
                conversationId: options.conversationId,
                messageId: options.messageId,
            };
            return { type: "data", name: "context-enrichment", data };
        }

        case "template_render":
            return {
                type: "data",
                name: "template-render",
                data: {
                    conversationId: block.conversationId,
                    messageId: block.messageId,
                },
            };

        default: {
            // Exhaustiveness check — fail the build if a new ContentBlock
            // union member lands without a branch here. Runtime fallback is
            // an empty text part rather than a crash.
            const _exhaustive: never = block;
            void _exhaustive;
            return { type: "text", text: "" };
        }
    }
}
